import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import uniqueId from "lodash/uniqueId";
import messages from "messages";
import moment from "moment-timezone";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Dropdown, FormControl } from "react-bootstrap";
import { createPortal } from "react-dom";
import getClasses from "utils/getClasses";
import { TIME_POINT_PLACEHOLDER } from "../../constants/time";
import { TIME_FORMAT_12 } from "../../utils/get12hrTime";
import styles from "./TimeSlotDropdown.module.scss";
import getValidTime from "./utils/getValidTime";

type Props = {
  dateValue: number;
  /** iso string */
  time?: string;
  /** iso string */
  min?: string;
  /** iso string */
  max?: string;
  /** iso string */
  onChange: (arg: string) => void;
  isFrom?: boolean;
  toggleClass?: string;
  /** if it is required */
  required?: boolean;
};

const ForwardedTimeSlotDropdown = forwardRef<
  Pick<HTMLFormElement, "checkValidity" | "reset">,
  Props
>(function TimeSlotDropdown(
  { dateValue, time, min, max, onChange, isFrom, toggleClass, required },
  ref,
) {
  const [invalid, setInvalid] = useState<boolean | undefined>(undefined);
  // implement a HTML5 form like validation functionality
  const checkValidity = useCallback(
    (val = time) => {
      if (required) {
        if (!val) {
          setInvalid(true);
          return false;
        }
      }

      setInvalid(false);
      return true;
    },
    [required, time],
  );

  useImperativeHandle(ref, () => ({
    checkValidity,
    reset: () => setInvalid(undefined),
  }));

  const { currentCalendarTimezone } = useCalendarTime();

  const options = useMemo(() => {
    const nextStartDate = moment
      .tz(dateValue, currentCalendarTimezone)
      .add(1, "days");
    const minValue = !min
      ? +new Date(dateValue)
      : +new Date(Math.max(+new Date(min), dateValue));
    const maxValue = !max
      ? +nextStartDate
      : +new Date(Math.min(+new Date(max), +nextStartDate));

    // add fallback for edge case
    if (minValue > maxValue) {
      return [];
    }
    return getValidTime(minValue, maxValue, isFrom);
  }, [dateValue, currentCalendarTimezone, min, max, isFrom]);

  return (
    <div className={styles["time-slot-dropdown"]}>
      <Dropdown>
        <Dropdown.Toggle
          className={getClasses(
            styles["time-slot-dropdown__toggle"],
            invalid && styles["time-slot-dropdown__toggle--invalid"],
            toggleClass,
          )}
          id={uniqueId("timeslot")}
          variant={"outline-primary"}
        >
          {/* time string */}
          {!time
            ? TIME_POINT_PLACEHOLDER
            : moment(time).tz(currentCalendarTimezone).format(TIME_FORMAT_12)}
        </Dropdown.Toggle>
        {/* render into body, otherwise will be hidden by scroll container */}
        {createPortal(
          <Dropdown.Menu className={styles["time-slot-dropdown__menu"]}>
            {options.map((el, i) => (
              <Dropdown.Item
                key={i}
                className={styles["time-slot-dropdown__item"]}
                onClick={() => {
                  onChange(el);

                  if (invalid === true) {
                    checkValidity(el);
                  }
                }}
              >
                {moment(el).tz(currentCalendarTimezone).format(TIME_FORMAT_12)}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>,
          document.body,
        )}
      </Dropdown>
      {invalid && (
        <FormControl.Feedback
          className={styles["time-slot-dropdown__invalid-feedback"]}
          type={"invalid"}
          aria-live={"polite"}
        >
          {isFrom ? messages.selectStartTime : messages.selectEndTime}
        </FormControl.Feedback>
      )}
    </div>
  );
});

export default ForwardedTimeSlotDropdown;
